import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Badge } from "shared/ui/badge"
import { Separator } from "shared/ui/separator"
import { Loader2, CheckCircle2, Clock, AlertTriangle } from "lucide-react"
import type { PermitWorkflowTimeline } from "modules/permit/api/permitService"

interface WorkflowTimelinePanelProps {
  timeline: PermitWorkflowTimeline | null
  isLoading: boolean
  error?: string | null
}

const statusBadgeClass: Record<string, string> = {
  PENDING: "bg-slate-100 text-slate-700 border border-slate-200",
  IN_PROGRESS: "bg-indigo-100 text-indigo-800 border border-indigo-200",
  APPROVED: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  REJECTED: "bg-rose-100 text-rose-800 border border-rose-200",
  RETURNED: "bg-orange-100 text-orange-800 border border-orange-200",
  SKIPPED: "bg-slate-100 text-slate-700 border border-slate-200",
  COMPLETED: "bg-emerald-100 text-emerald-800 border border-emerald-200",
}

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return "-"
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleString("th-TH", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
}

const toSortValue = (value?: string | null): number => {
  if (!value) {
    return Number.MIN_SAFE_INTEGER
  }

  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? Number.MIN_SAFE_INTEGER : parsed
}

const StatusBadge = ({ status }: { status: string }) => {
  const normalized = status.toUpperCase()
  const className =
    statusBadgeClass[normalized] ?? "bg-gray-100 text-gray-700 border border-gray-200"

  return (
    <Badge className={`text-[11px] ${className}`} variant="outline">
      {normalized}
    </Badge>
  )
}

const StageRow = ({
  stage,
  isCurrent,
  approverName,
}: {
  stage: NonNullable<PermitWorkflowTimeline["stages"]>[number]
  isCurrent: boolean
  approverName?: string | null
}) => {
  return (
    <div
      className={`rounded-md border px-3 py-2 ${isCurrent ? "border-indigo-300 bg-indigo-50/60" : "border-slate-200 bg-white"}`}
    >
      <div className="flex items-center justify-between gap-2 text-sm font-medium text-slate-900">
        <span>
          {stage.name || stage.code || `Stage ${stage.seqIndex}`}
          <span className="ml-2 text-xs text-slate-500">#{stage.seqIndex}</span>
        </span>
        <div className="flex items-center gap-2">
          <StatusBadge status={stage.status} />
        </div>
      </div>
      <div className="mt-1 text-xs text-slate-500">
        {approverName ? (
          <p>
            ผู้อนุมัติ: <span className="text-slate-800">{approverName}</span>
          </p>
        ) : null}
        <p>เริ่ม: {formatDateTime(stage.startedAt)}</p>
        <p>เสร็จ: {formatDateTime(stage.completedAt)}</p>
      </div>
    </div>
  )
}

const WorkflowHistoryRow = ({
  item,
}: {
  item: NonNullable<PermitWorkflowTimeline["history"]>[number]
}) => {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 font-medium text-slate-800">
          <span>{item.statusBefore ? `${item.statusBefore} → ${item.statusAfter}` : item.statusAfter}</span>
          <Badge variant="outline" className="text-[10px] text-slate-500">
            {item.isCurrentInstance ? "Workflow ล่าสุด" : "Workflow ก่อนหน้า"}
          </Badge>
        </div>
        <div className="text-[11px] text-slate-500">{formatDateTime(item.actedAt)}</div>
      </div>
      <div className="mt-1 text-slate-500">
        ขั้นตอน #{item.seqIndex} • ผู้ดำเนินการ:{" "}
        <span className="text-slate-800">
          {item.actedByUserName || item.actedByRoleName || "ไม่ระบุ"}
        </span>
      </div>
      {item.actionNote ? (
        <p className="mt-1 text-slate-500">หมายเหตุ: {item.actionNote}</p>
      ) : null}
    </div>
  )
}

const ExtendHistoryRow = ({
  item,
}: {
  item: NonNullable<PermitWorkflowTimeline["extensionHistory"]>[number]
}) => {
  return (
    <div className="rounded-md border border-blue-200 bg-blue-50/70 px-3 py-2 text-xs text-blue-900">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 font-medium">
          <span>ขอต่อ Permit</span>
          <Badge variant="outline" className="border-blue-200 bg-white text-[10px] text-blue-700">
            ต่อเวลาทำงาน
          </Badge>
        </div>
        <div className="text-[11px] text-blue-700">{formatDateTime(item.requestedAt)}</div>
      </div>
      <div className="mt-1 text-blue-800">
        ผู้ดำเนินการ: <span className="font-medium">{item.requestedByUserName || "ไม่ระบุ"}</span>
      </div>
      {item.previousEnd || item.newExtendedEnd ? (
        <div className="mt-1 text-blue-800">
          เวลาสิ้นสุดเดิม {formatDateTime(item.previousEnd)} → เวลาสิ้นสุดใหม่{" "}
          {formatDateTime(item.newExtendedEnd)}
        </div>
      ) : null}
      {item.reason ? <p className="mt-1 text-blue-800">เหตุผล: {item.reason}</p> : null}
    </div>
  )
}

export function WorkflowTimelinePanel({
  timeline,
  isLoading,
  error,
}: WorkflowTimelinePanelProps) {
  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex items-center gap-3 py-6 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
          กำลังโหลดข้อมูล Workflow...
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50 shadow-sm">
        <CardContent className="py-4 text-sm text-red-700">{error}</CardContent>
      </Card>
    )
  }

  if (!timeline) {
    return (
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">สถานะการอนุมัติ</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            ยังไม่มีการส่งอนุมัติสำหรับใบงานนี้
          </div>
        </CardContent>
      </Card>
    )
  }

  const groupedStages = new Map<number, typeof timeline.stages>()
  timeline.stages.forEach((stage) => {
    const key = stage.seqIndex ?? 0
    const existing = groupedStages.get(key)
    if (existing) {
      existing.push(stage)
      return
    }
    groupedStages.set(key, [stage])
  })

  const orderedGroups = Array.from(groupedStages.entries())
    .sort(([a], [b]) => a - b)
    .map(([seqIndex, stages]) => [seqIndex, stages.slice()] as const)
  const historyItems = timeline.history ?? []
  const currentInstanceHistoryItems = historyItems.filter((item) => item.isCurrentInstance)
  const extensionHistoryItems = timeline.extensionHistory ?? []
  const stageHistoryMap = new Map<string, typeof currentInstanceHistoryItems>()
  const combinedHistoryItems = [
    ...historyItems.map((item) => ({
      kind: "workflow" as const,
      sortValue: toSortValue(item.actedAt),
      item,
    })),
    ...extensionHistoryItems.map((item) => ({
      kind: "extension" as const,
      sortValue: toSortValue(item.requestedAt),
      item,
    })),
  ].sort((a, b) => b.sortValue - a.sortValue)

  const hasDecisionHistory = currentInstanceHistoryItems.some((item) => {
    const statusAfter = (item.statusAfter ?? "").toUpperCase()
    return (
      statusAfter === "APPROVED" ||
      statusAfter === "REJECTED" ||
      statusAfter === "RETURNED" ||
      statusAfter === "COMPLETED"
    )
  })

  const firstPendingSeq = orderedGroups.find(([, stages]) =>
    stages.some((stage) => (stage.status ?? "").toUpperCase() === "PENDING"),
  )?.[0]

  const visibleGroups =
    !hasDecisionHistory && typeof firstPendingSeq === "number"
      ? orderedGroups.filter(([seqIndex]) => seqIndex <= firstPendingSeq)
      : orderedGroups

  const normalizeKey = (value?: string | null) => (value ?? "").trim().toLowerCase()

  const getApproverName = (stage: NonNullable<PermitWorkflowTimeline["stages"]>[number]) => {
    const roleFallback =
      stage.assignedUserName || stage.assignedRoleUserNames || stage.assignedRoleName || stage.assignedRoleCode || null
    const stageId = normalizeKey(stage.stageId)
    if (!stageId) {
      return roleFallback
    }

    if (!stageHistoryMap.has(stageId)) {
      const ordered = currentInstanceHistoryItems
        .filter((item) => normalizeKey(item.stageId) === stageId)
        .slice()
        .sort((a, b) => {
          const timeA = new Date(a.actedAt).getTime()
          const timeB = new Date(b.actedAt).getTime()
          if (Number.isNaN(timeA) || Number.isNaN(timeB)) {
            return (b.actedAt ?? "").localeCompare(a.actedAt ?? "")
          }
          return timeB - timeA
        })
      stageHistoryMap.set(stageId, ordered)
    }

    const orderedHistory = stageHistoryMap.get(stageId) ?? []
    if (orderedHistory.length === 0) {
      return roleFallback
    }

    const stageStatus = (stage.status ?? "").toUpperCase()

    // A stage that is still IN_PROGRESS has no approver yet by definition.
    // The history row for that transition may still carry an actedByUserId
    // (e.g. whoever resubmitted the permit to reopen this stage) - that's
    // valid audit data, but it must never be shown as "the approver".
    if (stageStatus === "IN_PROGRESS") {
      return roleFallback
    }

    const matchByStatus = orderedHistory.find(
      (item) => (item.statusAfter ?? "").toUpperCase() === stageStatus,
    )
    const approvalFallback =
      stageStatus === "APPROVED" || stageStatus === "COMPLETED"
        ? orderedHistory.find((item) => {
            const status = (item.statusAfter ?? "").toUpperCase()
            return status === "APPROVED" || status === "COMPLETED"
          })
        : undefined
    const selected = matchByStatus ?? approvalFallback ?? orderedHistory[0]

    // A history entry with no actedByUserName only recorded a status transition,
    // not an actual person acting on it - prefer the current role holders' names
    // over that entry's generic role label.
    return selected?.actedByUserName || roleFallback || selected?.actedByRoleName
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-4 w-4 text-indigo-500" />
          สถานะการอนุมัติ
        </CardTitle>
        <div className="text-xs text-slate-500">
          เริ่มต้น: {formatDateTime(timeline.startedAt)} • เสร็จสิ้น:{" "}
          {formatDateTime(timeline.completedAt)}
        </div>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            <span className="font-semibold text-slate-900">ลำดับขั้นตอน</span>
          </div>
          <div className="space-y-3">
            {timeline.stages.length === 0 ? (
              <p className="text-xs text-slate-500">ยังไม่มีการกำหนดขั้นตอน</p>
            ) : (
              visibleGroups.map(([seqIndex, stages]) => (
                <div
                  key={`seq-${seqIndex}`}
                  className="rounded-lg border border-slate-200 bg-slate-50/80 p-3"
                >
                  <div className="mb-2 flex items-center justify-between text-xs font-semibold text-slate-600">
                    <span>ลำดับขั้นที่ {seqIndex}</span>
                    <span>{stages.length} รายการ</span>
                  </div>
                  <div className="space-y-2">
                    {stages.map((stage) => (
                      <StageRow
                        key={stage.permitApprovalId}
                        stage={stage}
                        isCurrent={(stage.status ?? "").toUpperCase() === "IN_PROGRESS"}
                        approverName={getApproverName(stage)}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        <Separator />

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate-500" />
            <span className="font-semibold text-slate-900">ประวัติการดำเนินการ</span>
          </div>
          {combinedHistoryItems.length === 0 ? (
            <p className="text-xs text-slate-500">ยังไม่มีประวัติการดำเนินการ</p>
          ) : (
            <div className="space-y-2">
              {combinedHistoryItems.map((entry) =>
                entry.kind === "workflow" ? (
                  <WorkflowHistoryRow
                    key={`workflow-${entry.item.permitWorkflowStageHistoryId}`}
                    item={entry.item}
                  />
                ) : (
                  <ExtendHistoryRow
                    key={`extend-${entry.item.requestedAt}-${entry.item.newExtendedEnd ?? ""}`}
                    item={entry.item}
                  />
                ),
              )}
            </div>
          )}
        </section>
      </CardContent>
    </Card>
  )
}
